import React, { useState, useEffect } from 'react';
import { getPendingApiRequests, approveRejectApiRequest } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { showSuccessToast, showErrorToast } from '../../utils/toast';
import { ChevronDown, ChevronUp, CheckCircle2, XCircle, Clock } from 'lucide-react';

// Group key: same merchant + same API prefix (e.g. "BBPS") = one card
const getPrefix = (apiCode = '') => apiCode.split('_')[0] || 'OTHER';

const GROUP_LABELS = {
  OTP: 'OTP Services', BBPS: 'BBPS Services', DMT: 'DMT Services',
  AEPS: 'AEPS Services', UPI: 'UPI Services', RECHARGE: 'Recharge Services',
  PAN: 'PAN Services', KYC: 'KYC Services', NEFT: 'NEFT Services',
  IMPS: 'IMPS Services', RTGS: 'RTGS Services', BANK: 'Banking Services',
  WALLET: 'Wallet Services', PAYMENT: 'Payment Services',
  REPORT: 'Report Services', OTHER: 'Other Services',
};
const getGroupLabel = (key) => GROUP_LABELS[key] || `${key} Services`;

const ApiRequests = () => {
  const [requests, setRequests]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState(null); // { groupKey, merchantId, merchantName, requests[] }
  const [remarks, setRemarks]     = useState('');
  const [processing, setProcessing] = useState(false);
  const [expanded, setExpanded]   = useState({});

  useEffect(() => { loadRequests(); }, []);

  const loadRequests = async () => {
    try {
      const res = await getPendingApiRequests();
      setRequests(res.data || []);
    } catch {
      showErrorToast('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  // Group by merchantId + apiPrefix
  const grouped = requests.reduce((acc, r) => {
    const prefix = getPrefix(r.apiCode);
    // Use merchant object if available, otherwise fallback to direct fields
    const merchantData = r.merchant || {};
    const merchantId = merchantData.merchantId || r.merchantId || r.merchantCode || 'unknown';
    const key = `${merchantId}__${prefix}`;
    if (!acc[key]) {
      acc[key] = {
        key,
        prefix,
        merchantId,
        merchantName: merchantData.merchantName || r.merchantName || r.merchantCode || merchantId,
        merchantEmail: merchantData.email || '',
        merchantPhone: merchantData.phone || '',
        merchantCompany: merchantData.companyName || '',
        portalUrl: r.portalUrl,
        useCase: r.useCase,
        requests: [],
      };
    }
    acc[key].requests.push(r);
    return acc;
  }, {});

  const groups = Object.values(grouped);

  // Approve or reject all requests in the group
  const handleApproval = async (approved) => {
    if (!selected) return;
    setProcessing(true);
    try {
      await Promise.all(
        selected.requests.map(r =>
          approveRejectApiRequest(r.requestId, { approved, remarks })
        )
      );
      showSuccessToast(
        approved
          ? `${getGroupLabel(selected.prefix)} approved for ${selected.merchantName}`
          : `${getGroupLabel(selected.prefix)} rejected`
      );
      setSelected(null);
      setRemarks('');
      loadRequests();
    } catch {
      showErrorToast('Failed to process request');
    } finally {
      setProcessing(false);
    }
  };

  const toggleExpand = (key) =>
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  if (loading) return (
    <div className="flex justify-center items-center p-8 gap-3">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
      <span className="text-gray-600">Loading requests...</span>
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">API Activation Requests</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {groups.length} pending group{groups.length !== 1 ? 's' : ''} · {requests.length} total endpoint{requests.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-lg">
          <Clock size={14} />
          <span>{requests.length} pending</span>
        </div>
      </div>

      {groups.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle2 className="mx-auto mb-3 text-green-500" size={40} />
            <p className="text-gray-500 font-medium">No pending requests</p>
            <p className="text-gray-400 text-sm mt-1">All API requests have been reviewed</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {groups.map(group => {
            const isOpen = !!expanded[group.key];
            return (
              <Card key={group.key} className="overflow-hidden">
                <CardContent className="p-0">
                  {/* Group header */}
                  <div className="flex items-center justify-between px-5 py-4">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <button
                        onClick={() => toggleExpand(group.key)}
                        className="text-gray-400 hover:text-gray-700 transition-colors flex-shrink-0"
                      >
                        {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                      </button>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-gray-900">{getGroupLabel(group.prefix)}</span>
                          <span className="text-xs font-mono bg-gray-100 text-gray-500 px-2 py-0.5 rounded">
                            {group.prefix}
                          </span>
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                            {group.requests.length} endpoint{group.requests.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 mt-0.5">
                          <p className="text-sm text-gray-500">
                            Merchant: <span className="font-medium text-gray-700">{group.merchantName}</span>
                            {group.merchantId && <span className="text-xs text-gray-400 ml-1">({group.merchantId})</span>}
                          </p>
                          {group.merchantCompany && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                              {group.merchantCompany}
                            </span>
                          )}
                        </div>
                        {(group.merchantEmail || group.merchantPhone) && (
                          <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400">
                            {group.merchantEmail && (
                              <span>📧 {group.merchantEmail}</span>
                            )}
                            {group.merchantPhone && (
                              <span>📱 {group.merchantPhone}</span>
                            )}
                          </div>
                        )}
                        {group.portalUrl && (
                          <a
                            href={group.portalUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-blue-500 hover:underline truncate max-w-[180px] inline-block mt-0.5"
                          >
                            🔗 {group.portalUrl}
                          </a>
                        )}
                        {group.useCase && (
                          <p className="text-xs text-gray-400 mt-0.5 truncate max-w-md">
                            Use case: {group.useCase}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Single Review button for whole group */}
                    <Button
                      size="sm"
                      onClick={() => { setSelected(group); setRemarks(''); }}
                      className="flex-shrink-0 ml-4"
                    >
                      Review All
                    </Button>
                  </div>

                  {/* Expandable endpoint list */}
                  {isOpen && (
                    <div className="border-t border-gray-100 bg-gray-50">
                      {/* Sub-header */}
                      <div className="grid grid-cols-3 gap-2 px-6 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wide border-b border-gray-100">
                        <span>API Name</span>
                        <span>Code</span>
                        <span>Request ID</span>
                      </div>
                      {group.requests.map((r, idx) => (
                        <div
                          key={r.requestId || idx}
                          className={`grid grid-cols-3 gap-2 px-6 py-2.5 text-sm items-center ${
                            idx < group.requests.length - 1 ? 'border-b border-gray-100' : ''
                          }`}
                        >
                          <span className="text-gray-700">{r.apiName}</span>
                          <span className="font-mono text-xs text-gray-500">{r.apiCode}</span>
                          <span className="font-mono text-xs text-gray-400">{r.requestId}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Review Dialog */}
      <Dialog open={!!selected} onOpenChange={() => { setSelected(null); setRemarks(''); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Review — {selected ? getGroupLabel(selected.prefix) : ''}
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 mt-1">
              {/* Summary */}
              <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1.5">
                <div className="flex justify-between">
                  <span className="text-gray-500">Merchant</span>
                  <span className="font-medium text-gray-800">{selected.merchantName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Service</span>
                  <span className="font-medium text-gray-800">{getGroupLabel(selected.prefix)}</span>
                </div>
                {selected.portalUrl && (
                  <div className="flex justify-between gap-4">
                    <span className="text-gray-500 flex-shrink-0">Portal URL</span>
                    <a href={selected.portalUrl} target="_blank" rel="noreferrer"
                      className="text-blue-500 hover:underline text-right truncate">
                      {selected.portalUrl}
                    </a>
                  </div>
                )}
                {selected.useCase && (
                  <div>
                    <span className="text-gray-500">Use Case</span>
                    <p className="text-gray-700 mt-0.5">{selected.useCase}</p>
                  </div>
                )}
              </div>

              {/* Endpoints being approved */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Endpoints ({selected.requests.length})
                </p>
                <div className="border border-gray-200 rounded-lg divide-y max-h-40 overflow-y-auto">
                  {selected.requests.map((r, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                      <span className="text-gray-700">{r.apiName}</span>
                      <span className="font-mono text-xs text-gray-400">{r.apiCode}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Remarks */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Remarks <span className="text-gray-400 font-normal">(optional)</span></p>
                <Textarea
                  placeholder="Add remarks for this decision..."
                  value={remarks}
                  onChange={e => setRemarks(e.target.value)}
                  rows={3}
                />
              </div>

              {/* Action buttons */}
              <div className="flex gap-2 pt-1">
                <Button
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white gap-1.5"
                  onClick={() => handleApproval(true)}
                  disabled={processing}
                >
                  <CheckCircle2 size={15} />
                  {processing ? 'Processing...' : 'Approve All'}
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1 gap-1.5"
                  onClick={() => handleApproval(false)}
                  disabled={processing}
                >
                  <XCircle size={15} />
                  {processing ? 'Processing...' : 'Reject All'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApiRequests;